import React, { useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Eye } from 'lucide-react';

// Read from localStorage key 'trialAssignments' if available;
// TrialCreation persists its mock assignments there on change.

const loadAssignments = () => {
  try {
    const raw = window.localStorage.getItem('trialAssignments');
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

// no-op helper retained for possible future detailed view

const ViewAssignment = () => {
  const { user } = useAuth();
  const assignments = useMemo(loadAssignments, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCentre, setFilterCentre] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [selectedTrialCode, setSelectedTrialCode] = useState('');
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const centres = useMemo(() => {
    const set = new Set((assignments || []).map(a => a.locationName || ''));
    return Array.from(set).filter(Boolean).sort();
  }, [assignments]);

  const getStatusColor = (status) => {
    return status === 'inactive'
      ? 'bg-gray-100 text-gray-800'
      : 'bg-green-100 text-green-800';
  };

  const filteredAssignments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (assignments || [])
      .filter(a => {
        // if officer logged in, show only assignments where he/she is incharge (assignedOfficer)
        if (user?.role === 'officer' || user?.role === 'plot-officer') {
          return (a.assignedOfficer || '').toLowerCase() === (user?.name || '').toLowerCase();
        }
        return true;
      })
      .filter(a => {
      const code = (a.locationCode || '').slice(0, -3);
      const status = (a.status || 'active').toLowerCase();
      const centre = (a.locationName || '').toLowerCase();
      const obs = (a.observations || []).join(', ').toLowerCase();
      const matchesSearch = !term || code.toLowerCase().includes(term) || centre.includes(term) || obs.includes(term);
      const matchesCentre = filterCentre === 'all' || (a.locationName || '') === filterCentre;
      const matchesStatus = filterStatus === 'all' || status === filterStatus;
      return matchesSearch && matchesCentre && matchesStatus;
    });
  }, [assignments, searchTerm, filterCentre, filterStatus]);

  const openTrialModal = (trialCode) => {
    setSelectedTrialCode(trialCode);
    setShowTrialModal(true);
  };

  const trialAssignments = useMemo(() => {
    if (!selectedTrialCode) return [];
    return (assignments || []).filter(a => ((a.locationCode || '').slice(0, -3)) === selectedTrialCode);
  }, [assignments, selectedTrialCode]);

  const openAssignmentDetail = (assignment) => {
    setSelectedAssignment(assignment);
    setShowAssignmentModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">View Assignments</h1>
      </div>

      {/* Search and Filters */}
      <div className="space-y-3">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M12.9 14.32a8 8 0 111.414-1.414l3.387 3.387a1 1 0 01-1.414 1.414l-3.387-3.387zM14 8a6 6 0 11-12 0 6 6 0 0112 0z" clipRule="evenodd" />
          </svg>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by trial code, centre, or observations..."
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={filterCentre}
            onChange={(e) => setFilterCentre(e.target.value)}
            className="input-field sm:w-56"
          >
            <option value="all">All Centres</option>
            {centres.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input-field sm:w-40"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Trial-level table */}
      <div className="card p-6">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-gray-700">Date</th>
              <th className="px-4 py-2 text-left text-gray-700">Trial Code</th>
                <th className="px-4 py-2 text-left text-gray-700">Centre Name</th>
              <th className="px-4 py-2 text-left text-gray-700">Observations</th>
                <th className="px-4 py-2 text-left text-gray-700">Status</th>
              <th className="px-4 py-2 text-right text-gray-700">Action</th>
            </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAssignments.map(a => (
              <tr key={a.id}>
                <td className="px-4 py-2">{a.assignedDate || '-'}</td>
                <td className="px-4 py-2 font-mono">{(a.locationCode || '').slice(0, -3)}</td>
                  <td className="px-4 py-2">{a.locationName || 'Centre'}</td>
                <td className="px-4 py-2 whitespace-pre-wrap break-words">{(a.observations || []).join(', ')}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(a.status || 'active')}`}>
                      {(a.status || 'active')}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => openTrialModal((a.locationCode || '').slice(0, -3))} className="inline-flex items-center text-primary-600 hover:underline">
                      <Eye className="w-4 h-4 mr-1" /> View
                    </button>
                  </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Trial -> Locations Modal */}
      {showTrialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Locations for Trial: <span className="font-mono text-blue-600">{selectedTrialCode}</span></h2>
                <button onClick={() => setShowTrialModal(false)} className="btn-secondary">Close</button>
              </div>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-gray-700">Location Name</th>
                      <th className="px-4 py-2 text-left text-gray-700">Plot Officer</th>
                      <th className="px-4 py-2 text-left text-gray-700">Observations</th>
                      <th className="px-4 py-2 text-left text-gray-700">Status</th>
                      <th className="px-4 py-2 text-right text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {trialAssignments.map((a) => (
                      <tr key={a.id}>
                        <td className="px-4 py-2">{a.locationName}</td>
                        <td className="px-4 py-2">{a.assignedOfficer || '-'}</td>
                        <td className="px-4 py-2 whitespace-pre-wrap break-words">{(a.observations || []).join(', ')}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(a.status || 'active')}`}>{a.status || 'active'}</span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button onClick={() => openAssignmentDetail(a)} className="inline-flex items-center text-primary-600 hover:underline">
                            <Eye className="w-4 h-4 mr-1" /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Detail Modal */}
      {showAssignmentModal && selectedAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Assignment Details</h2>
                <button onClick={() => setShowAssignmentModal(false)} className="btn-secondary">Close</button>
              </div>

              {/* Trial Details */}
              <div>
                <h3 className="text-md font-semibold text-gray-900 mb-2">Trial Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Trial Code</p>
                    <p className="text-sm text-gray-900 font-mono">{(selectedAssignment.locationCode || '').slice(0, -3)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Observations</p>
                    <p className="text-sm text-gray-900">{(selectedAssignment.observations || []).join(', ')}</p>
                  </div>
                </div>
              </div>

              {/* Location Details */}
              <div>
                <h3 className="text-md font-semibold text-gray-900 mb-2">Location Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Location Code</p>
                    <p className="text-sm text-gray-900 font-mono">{selectedAssignment.locationCode}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Location Name</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.locationName}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Assigned Scientist</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.assignedScientist || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Plot Officer</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.assignedOfficer || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Latitude</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.latitude || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Longitude</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.longitude || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Altitude (m)</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.altitude || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">SF Number</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.sfNumber || '-'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-gray-700">Address</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.locationAddress || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Status</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.status || 'active'}</p>
                  </div>
                </div>
              </div>

              {/* Individual Location Trial Details (if captured) */}
              <div>
                <h3 className="text-md font-semibold text-gray-900 mb-2">Individual Location Trial Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Experimental Area (ha)</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.experimentalArea ?? '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Plot Size of Each Culture (m²)</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.plotSize ?? '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Soil Type</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.soilType || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Soil Test Date</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.soilTestDate || '-'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-gray-700">Soil Test Details</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.soilTestDetails || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Source of Irrigation</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.irrigationSource || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Previous Crop Raised</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.previousCrop || '-'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-gray-700">Seed Treatment Details</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.seedTreatment || '-'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-gray-700">Ploughing</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.ploughing || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Date of Sowing</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.sowingDate || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Date of Planting</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.plantingDate || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Basal Organic Name</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.basalOrganic || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Basal Organic Date</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.basalOrganicDate || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Basal Organic Quantity (kg/ha)</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.basalOrganicQuantity ?? '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Basal Fertilizer Date</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.basalFertilizerDate || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Basal Fertilizer Name</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.basalFertilizerName || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Basal Fertilizer Quantity (kg/ha)</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.basalFertilizerQuantity ?? '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Top Dressing Date</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.topDressingDate || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Top Dressing Name</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.topDressingName || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Top Dressing Quantity (kg/ha)</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.topDressingQuantity ?? '-'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-gray-700">Weed Management Details</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.weedManagement || '-'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-gray-700">Plant Protection Details</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.plantProtection || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Date of Harvest</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.harvestDate || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Monitoring Team Visit Date</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.monitoringVisitDate || '-'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-gray-700">Monitoring Team Comments</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.monitoringComments || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Plot Officer Visit Date</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.plotOfficerVisitDate || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Date of Field Day Conducted</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.fieldDayDate || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Inspection Officer Visit Date</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.inspectionVisitDate || '-'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-gray-700">Inspection Officer Comments</p>
                    <p className="text-sm text-gray-900">{selectedAssignment.inspectionComments || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Plot Officer Data Entries */}
              {Array.isArray(selectedAssignment.dataEntries) && selectedAssignment.dataEntries.length > 0 && (
                <div>
                  <h3 className="text-md font-semibold text-gray-900 mb-2">Plot Officer Data Entries</h3>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-700">Date</th>
                          <th className="px-4 py-2 text-left text-gray-700">Culture</th>
                          <th className="px-4 py-2 text-left text-gray-700">Observation</th>
                          <th className="px-4 py-2 text-left text-gray-700">Replication Summary</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedAssignment.dataEntries.map((e) => (
                          <tr key={e.id}>
                            <td className="px-4 py-2">{e.entryDate || '-'}</td>
                            <td className="px-4 py-2">{e.culture}</td>
                            <td className="px-4 py-2">{e.observation}</td>
                            <td className="px-4 py-2">{(e.replicationData || []).map((r, idx) => `R${idx + 1}: ${r?.value ?? [r?.v1, r?.v2, r?.v3].filter(v=>v!==undefined&&v!==null).join('/')}`).join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Only the main table is kept as requested */}
    </div>
  );
};

export default ViewAssignment;

